import { useEffect, useMemo, useRef, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  ChevronDownIcon,
  SearchIcon,
  Loader2,
  Type,
  Hash,
} from "lucide-react";
import { useGetUnifiedCampaignsQuery } from "@/services/campaign";
import { CampaignType, type IUnifiedCampaign } from "@/@types/campaign";
import { getCampaignTypeLabel } from "@/utils/campaign-types";

export interface SelectedCampaign {
  type: CampaignType;
  code: number;
  description: string;
  storeCount: number;
}

type SearchBy = "name" | "code";

const COMPARABLE_TYPES = [
  CampaignType.PRO013,
  CampaignType.PRO014,
  CampaignType.PRO017,
];

interface CampaignComboboxProps {
  /** Tipos selecionados no filtro (controla a busca). */
  campaignTypes: CampaignType[];
  /** Rótulo do gatilho quando já há campanha selecionada (deep-link). */
  triggerLabel?: string;
  onSelect: (campaign: SelectedCampaign) => void;
}

/**
 * Combobox de campanha com busca por NOME ou CÓDIGO (server-side via
 * /campaigns/unified), respeitando o filtro de tipo de campanha — mesmo padrão
 * da tela de listagem de campanhas.
 */
export function CampaignCombobox({
  campaignTypes,
  triggerLabel,
  onSelect,
}: CampaignComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchBy, setSearchBy] = useState<SearchBy>("name");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Debounce da busca (350ms) para não disparar query a cada tecla
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!isOpen) setQuery("");
    const t = setTimeout(() => {
      if (isOpen) searchRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [isOpen]);

  const activeTypes = campaignTypes.length ? campaignTypes : COMPARABLE_TYPES;

  const { data, isFetching } = useGetUnifiedCampaignsQuery(
    {
      meta: { page: 1, perPage: 50 },
      campaignTypes: activeTypes,
      ...(debouncedQuery &&
        (searchBy === "name"
          ? { search: debouncedQuery }
          : { campaignCode: debouncedQuery })),
    },
    { skip: !isOpen }
  );

  // Dedupe defensivo por type+code (unified pode trazer linhas por loja)
  const options = useMemo(() => {
    const seen = new Set<string>();
    const list: IUnifiedCampaign[] = [];
    for (const c of data?.data ?? []) {
      const key = `${c.type}-${c.code}`;
      if (seen.has(key)) continue;
      seen.add(key);
      list.push(c);
    }
    return list;
  }, [data]);

  const handleSelect = (c: IUnifiedCampaign) => {
    onSelect({
      type: c.type,
      code: c.code,
      description: c.description,
      storeCount: c.storeCount ?? 0,
    });
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          ref={buttonRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={cn(
            "flex h-9 w-[360px] items-center rounded-md border px-3 text-left text-sm transition-colors",
            isOpen
              ? "border-yellow-500 bg-muted"
              : "border-input bg-background hover:border-yellow-500"
          )}
          onClick={() => setIsOpen((o) => !o)}
        >
          <span className="flex-1 truncate">
            {triggerLabel || "Buscar campanha..."}
          </span>
          <ChevronDownIcon className="ml-1 h-5 w-5 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        style={{
          minWidth: buttonRef.current
            ? `${buttonRef.current.offsetWidth}px`
            : "auto",
          maxHeight: "380px",
          overflow: "hidden",
        }}
      >
        <div className="flex flex-col">
          {/* Seletor de tipo de busca (Nome / Código) + input */}
          <div className="sticky top-0 z-10 border-b bg-background p-2">
            <div className="mb-2 flex items-center gap-1">
              <Button
                type="button"
                variant={searchBy === "name" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={() => setSearchBy("name")}
              >
                <Type className="h-3.5 w-3.5" />
                Nome
              </Button>
              <Button
                type="button"
                variant={searchBy === "code" ? "secondary" : "ghost"}
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={() => setSearchBy("code")}
              >
                <Hash className="h-3.5 w-3.5" />
                Código
              </Button>
            </div>
            <div className="relative">
              <SearchIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  searchBy === "name"
                    ? "Pesquisar pelo nome da campanha..."
                    : "Pesquisar pelo código da campanha..."
                }
                className="h-9 pl-8"
                aria-label="Buscar campanha"
                inputMode={searchBy === "code" ? "numeric" : "text"}
              />
            </div>
          </div>

          <div
            role="listbox"
            aria-label="Campanhas"
            className="max-h-[270px] overflow-y-auto"
          >
            {isFetching ? (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Buscando...
              </div>
            ) : options.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                Nenhuma campanha encontrada
              </div>
            ) : (
              options.map((c) => (
                <button
                  key={`${c.type}-${c.code}`}
                  role="option"
                  aria-selected={false}
                  type="button"
                  className="flex w-full flex-col px-4 py-2 text-left transition-colors hover:bg-muted"
                  onClick={() => handleSelect(c)}
                >
                  <span className="truncate font-medium">
                    {c.description || "(sem nome)"}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {c.type} #{c.code} — {getCampaignTypeLabel(c.type)}
                    {c.storeCount ? ` (${c.storeCount} lojas)` : ""}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
